import type { AcpUsageData } from '@crowdex/agent-contracts';
import { cn } from '@crowdex/ui/lib/utils';
import { formatCost } from '@/lib/desktop-summary/format';
import { compactUsage } from '@/lib/environment-menu/format';

/** How full the context window is, from 0 to 1. */
export function usageRatio(usage: AcpUsageData): number {
  return Math.min(1, Math.max(0, usage.used / Math.max(usage.size, 1)));
}

/** The tone of a filling window: running, then amber when nearly full, red when full. */
function usageTone(ratio: number): string {
  return ratio >= 0.95 ? 'text-destructive' : ratio >= 0.85 ? 'text-attention' : 'text-running';
}

/** A ring filled to `ratio`, the conversation's context usage at a glance. */
export function UsageRingGlyph({ ratio, className }: { ratio: number; className?: string }) {
  const radius = 6;
  const circumference = 2 * Math.PI * radius;
  return (
    <svg aria-hidden className={cn('size-4 shrink-0 -rotate-90', className)} viewBox="0 0 16 16">
      <title>Context usage</title>
      <circle className="stroke-control" cx="8" cy="8" fill="none" r={radius} strokeWidth="2" />
      <circle
        className={cn('stroke-current', usageTone(ratio))}
        cx="8"
        cy="8"
        data-usage-ratio={ratio}
        fill="none"
        r={radius}
        strokeDasharray={`${ratio * circumference} ${circumference}`}
        strokeLinecap="round"
        strokeWidth="2"
      />
    </svg>
  );
}

/**
 * The header's usage ring with its percentage (D5): opens the model popover, where the context
 * window and the plan limits are detailed (D8).
 */
export function UsageRing({ usage, onOpen }: { usage: AcpUsageData; onOpen: () => void }) {
  const ratio = usageRatio(usage);
  const percentage = Math.round(ratio * 100);
  return (
    <button
      aria-label={`Context usage: ${percentage}%`}
      className="type-meta flex h-8 shrink-0 items-center gap-1.5 rounded-control px-2 text-muted-foreground tabular-nums transition-colors hover:bg-control hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={onOpen}
      title={`${compactUsage(usage.used)} of ${compactUsage(usage.size)} tokens${
        usage.cost === null ? '' : ` · ${formatCost(usage.cost)}`
      }`}
      type="button"
    >
      <UsageRingGlyph ratio={ratio} />
      {percentage}%
    </button>
  );
}
